import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { verseService, linkedVersesService, reviewHistoryService, reflectionService } from '../../../services/supabaseService';
import { bibleService } from '../../../services/bibleService';
import { useAuth } from '../../../contexts/AuthContext';
import { checkAndGrantAwards } from '../../../services/awardService';
import { useAwardToast } from '../../../contexts/AwardToastContext';

// Skeleton loader for VerseDetailModal
function VerseDetailSkeleton() {
  return (
    <div className="p-6 space-y-4 animate-pulse">
      <div className="h-5 bg-muted rounded w-2/3" />
      <div className="h-20 bg-muted rounded-lg" />
      <div className="grid grid-cols-2 gap-4">
        <div className="h-24 bg-muted rounded-lg" />
        <div className="h-24 bg-muted rounded-lg" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="h-16 bg-muted rounded-lg" />
        <div className="h-16 bg-muted rounded-lg" />
        <div className="h-16 bg-muted rounded-lg" />
      </div>
    </div>
  );
}


const VerseDetailModal = ({ verse, isOpen, onClose, onUpdate, onDelete, onSave }) => {
  const [notes, setNotes] = useState(verse?.notes || '');
  const [tags, setTags] = useState(verse?.tags?.join(', ') || '');
  const [activeTab, setActiveTab] = useState('details');
  const [notesIsPrivate, setNotesIsPrivate] = useState(true);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaveMessage, setNotesSaveMessage] = useState('');
  const { user } = useAuth();
  const { showAwardToast } = useAwardToast();
  const [contentVisible, setContentVisible] = useState(false);

  // New states for spaced repetition modification
  const [currentPhase, setCurrentPhase] = useState(verse?.currentPhase ?? 1);
  const [reviewCount, setReviewCount] = useState(verse?.reviewCount || 0);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isUpdatingPhase, setIsUpdatingPhase] = useState(false);
  const [phaseError, setPhaseError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Linked verses state
  const [linkedVerses, setLinkedVerses] = useState([]);
  const [linkedVersesLoading, setLinkedVersesLoading] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [linkSearchResults, setLinkSearchResults] = useState([]);
  const [linkSearchLoading, setLinkSearchLoading] = useState(false);
  const [linkRelationship, setLinkRelationship] = useState('Related');
  const [selectedLinkVerse, setSelectedLinkVerse] = useState(null);
  const [addLinkError, setAddLinkError] = useState('');

  // History state
  const [reviewHistory, setReviewHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Update states when verse changes
  useEffect(() => {
    if (verse) {
      setNotes(verse?.notes || '');
      setTags(verse?.tags?.join(', ') || '');
      setCurrentPhase(verse?.currentPhase ?? 1);
      setReviewCount(verse?.reviewCount || 0);
      setNotesSaveMessage('');
    }
  }, [verse]);

  // Fade-slide-in reveal when modal opens
  useEffect(() => {
    if (isOpen) {
      setContentVisible(false);
      const t = setTimeout(() => setContentVisible(true), 80);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Load linked verses when tab is active
  useEffect(() => {
    if (activeTab === 'links' && verse?.id) {
      loadLinkedVerses();
    }
  }, [activeTab, verse?.id]);

  // Load review history when tab is active
  useEffect(() => {
    if (activeTab === 'history' && verse?.id) {
      loadReviewHistory();
    }
  }, [activeTab, verse?.id]);

  if (!isOpen || !verse) return null;

  const loadLinkedVerses = async () => {
    setLinkedVersesLoading(true);
    const { data } = await linkedVersesService?.getLinkedVerses(verse?.id);
    setLinkedVerses(data || []);
    setLinkedVersesLoading(false);
  };

  const loadReviewHistory = async () => {
    setHistoryLoading(true);
    const { data } = await reviewHistoryService?.getVerseReviewHistory(verse?.id);
    setReviewHistory(data || []);
    setHistoryLoading(false);
  };

  const handleLinkSearch = async (query) => {
    setLinkSearchQuery(query);
    setSelectedLinkVerse(null);
    if (!query?.trim() || query?.trim()?.length < 2) {
      setLinkSearchResults([]);
      return;
    }
    setLinkSearchLoading(true);
    try {
      // Search the entire Bible using the comprehensive search function
      const { data } = await bibleService?.searchVerses(query, 15);
      if (data?.length > 0) {
        setLinkSearchResults((data || [])?.map(v => ({
          id: v?.verse_id,
          reference: v?.reference,
          text: v?.text,
          version: 'KJV',
          fromBible: true
        })));
      } else {
        setLinkSearchResults([]);
      }
    } catch (err) {
      console.error('Link search error:', err);
      setLinkSearchResults([]);
    } finally {
      setLinkSearchLoading(false);
    }
  };

  const handleAddLink = async () => {
    if (!selectedLinkVerse) {
      setAddLinkError('Please select a verse to link.');
      return;
    }
    setAddLinkError('');

    // Always use addLinkedVerseByReference since we're linking to any Bible verse
    const result = await linkedVersesService?.addLinkedVerseByReference(
      user?.id,
      verse?.id,
      selectedLinkVerse?.reference,
      selectedLinkVerse?.text,
      selectedLinkVerse?.version || 'KJV',
      linkRelationship
    );

    if (result?.error) {
      setAddLinkError(result?.error?.message || 'Failed to add link');
      return;
    }
    setShowAddLink(false);
    setLinkSearchQuery('');
    setLinkSearchResults([]);
    setSelectedLinkVerse(null);
    setLinkRelationship('Related');
    await loadLinkedVerses();
  };

  const handleRemoveLink = async (linkId) => {
    await linkedVersesService?.removeLinkedVerse(linkId);
    await loadLinkedVerses();
  };

  const handlePhaseChange = async (newPhase) => {
    if (!verse?.id) return;
    setIsUpdatingPhase(true);
    setPhaseError('');
    setSuccessMessage('');
    try {
      if (newPhase === 0) {
        await verseService?.moveVerseToFuture(verse?.id);
        setSuccessMessage('Verse moved to Future successfully');
      } else if (verse?.currentPhase === 0 && newPhase > 0) {
        await verseService?.startVerseMemorization(verse?.id);
        if (newPhase !== 1) {
          await verseService?.updateVersePhase(verse?.id, newPhase, verse?.reviewCount || 0);
        }
        setSuccessMessage(`Verse memorization started at Phase ${newPhase}`);
      } else {
        await verseService?.updateVersePhase(verse?.id, newPhase, verse?.reviewCount || 0);
        setSuccessMessage(`Verse phase updated to ${newPhase === 0 ? 'Future' : `Phase ${newPhase}`}`);
      }
      setCurrentPhase(newPhase);
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error updating verse phase:', error);
      setPhaseError(error?.message || 'Failed to update verse phase');
    } finally {
      setIsUpdatingPhase(false);
    }
  };

  const handleReviewCountChange = async (newCount) => {
    if (!verse?.id) return;
    setIsUpdatingPhase(true);
    setPhaseError('');
    setSuccessMessage('');
    try {
      await verseService?.updateVersePhase(verse?.id, verse?.currentPhase || 0, Math.max(0, newCount));
      setReviewCount(Math.max(0, newCount));
      if (onUpdate) await onUpdate();
      setSuccessMessage('Review count updated successfully');
    } catch (error) {
      console.error('Error updating review count:', error);
      setPhaseError(error?.message || 'Failed to update review count');
    } finally {
      setIsUpdatingPhase(false);
    }
  };

  const handleSave = async () => {
    try {
      const updatedVerse = {
        ...verse,
        notes,
        tags: tags?.split(',')?.map(tag => tag?.trim())?.filter(tag => tag),
        ...(isEditing && { currentPhase, reviewCount })
      };

      const dbUpdates = {
        notes: updatedVerse?.notes,
        tags: updatedVerse?.tags,
        is_favorite: updatedVerse?.isFavorite,
        ...(isEditing && { current_phase: currentPhase, review_count: reviewCount })
      };

      if (onSave) {
        await onSave(verse?.id, dbUpdates, updatedVerse);
      }

      // Tag award checks
      const newTags = updatedVerse?.tags || [];
      const prevTags = verse?.tags || [];
      const addedTagCount = newTags?.length - prevTags?.length;
      if (addedTagCount > 0 && user?.id) {
        try {
          const { supabase } = await import('../../../lib/supabase');
          const { data: allVerses } = await supabase?.from('verses')?.select('tags')?.eq('user_id', user?.id);
          const totalTags = (allVerses || [])?.reduce((sum, v) => sum + (v?.tags?.length || 0), 0);
          const [tagCreatedAwards, tagMilestoneAwards] = await Promise.all([
            checkAndGrantAwards(user?.id, 'TAG_CREATED', {}),
            checkAndGrantAwards(user?.id, 'TAG_MILESTONE', { totalTags })
          ]);
          [...tagCreatedAwards, ...tagMilestoneAwards]?.forEach(a => showAwardToast(a));
        } catch (awardErr) {
          console.warn('Tag award check failed:', awardErr);
        }
      }

      if (onUpdate) await onUpdate();
      setIsEditing(false);
      setSuccessMessage('Verse updated successfully');
    } catch (error) {
      console.error('Error saving verse:', error);
      setPhaseError(error?.message || 'Failed to save verse');
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(verse?.id);
      setDeleteConfirmOpen(false);
      onClose();
    }
  };

  // Helper: calculate days since last review
  const getLastReviewedLabel = (lastReviewedAt) => {
    if (!lastReviewedAt) return '—';
    const now = new Date();
    const last = new Date(lastReviewedAt);
    const diffMs = now - last;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1d ago';
    return `${diffDays}d ago`;
  };

  const getLastReviewedColor = (lastReviewedAt) => {
    if (!lastReviewedAt) return 'text-muted-foreground';
    const now = new Date();
    const last = new Date(lastReviewedAt);
    const diffDays = Math.floor((now - last) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'text-success';
    if (diffDays <= 30) return 'text-warning';
    return 'text-destructive';
  };

  const handleSaveNotes = async () => {
    if (!user?.id || !verse?.id) return;
    setNotesSaving(true);
    setNotesSaveMessage('');
    try {
      // Check if a reflection already exists for this verse + user
      const { supabase } = await import('../../../lib/supabase');
      const { data: existing } = await supabase
        ?.from('verse_reflections')
        ?.select('id')
        ?.eq('user_id', user?.id)
        ?.eq('verse_id', verse?.id)
        ?.maybeSingle();

      if (existing?.id) {
        await supabase
          ?.from('verse_reflections')
          ?.update({
            meditation_text: notes,
            is_private: notesIsPrivate,
            updated_at: new Date()?.toISOString()
          })
          ?.eq('id', existing?.id);
      } else {
        await reflectionService?.createReflection({
          user_id: user?.id,
          verse_id: verse?.id,
          verse_reference: verse?.reference,
          verse_text: verse?.text,
          meditation_text: notes,
          is_private: notesIsPrivate
        });
      }
      setNotesSaveMessage('Notes saved successfully');
    } catch (err) {
      console.error('Error saving notes as reflection:', err);
      setNotesSaveMessage('Failed to save notes');
    } finally {
      setNotesSaving(false);
    }
  };

  // Calculate real progress
  const phaseReviewsCompleted = { 0: 0, 1: 0, 2: 30, 3: 40, 4: 46, 5: 49, 6: 52, 7: 54, 8: 70 };
  const progressPhase = verse?.currentPhase ?? 0;
  const progressInPhase = verse?.successfulReviewsInPhase || 0;
  const totalProgressReviews = (phaseReviewsCompleted?.[progressPhase] || 0) + progressInPhase;
  const progressPercentage = Math.min(100, Math.round((totalProgressReviews / 70) * 100));

  // Memorization status derived from phase
  const getMemorizationStatus = (phase) => {
    if (phase === 0) return { label: 'Not Started', color: 'text-muted-foreground', icon: 'Plus' };
    if (phase <= 2) return { label: 'Learning', color: 'text-warning', icon: 'Clock' };
    if (phase <= 5) return { label: 'Practicing', color: 'text-primary', icon: 'Brain' };
    if (phase <= 6) return { label: 'Consolidating', color: 'text-accent-foreground', icon: 'TrendingUp' };
    return { label: 'Mastered', color: 'text-success', icon: 'CheckCircle2' };
  };
  const memStatus = getMemorizationStatus(verse?.currentPhase ?? 0);

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 1: return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 2: return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 3: return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 4: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 5: return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300';
      case 6: return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 7: return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const formatInterval = (interval) => {
    if (!interval) return 'Not set';
    if (interval < 1) return `${Math.round(interval * 24)}h`;
    if (interval === 1) return '1 day';
    if (interval < 30) return `${Math.round(interval)} days`;
    if (interval < 90) return `${Math.round(interval / 30)} month${interval >= 60 ? 's' : ''}`;
    return `${Math.round(interval / 30)} months`;
  };

  const formatHistoryDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatHistoryTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getResponseBadge = (response) => {
    switch (response) {
      case 'good': return 'bg-success/15 text-success border border-success/30';
      case 'hard': return 'bg-warning/15 text-warning border border-warning/30';
      case 'missed': return 'bg-destructive/15 text-destructive border border-destructive/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const tabs = [
    { id: 'details', label: 'Details', icon: 'Info' },
    { id: 'spaced-repetition', label: 'Spaced Rep', icon: 'Brain' },
    { id: 'notes', label: 'Notes', icon: 'StickyNote' },
    { id: 'links', label: 'Linked Verses', icon: 'Link' },
    { id: 'history', label: 'History', icon: 'Clock' }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div
        className={`bg-card rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden shadow-2xl border border-border transition-all duration-300 ${
          contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name="BookOpen" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">
                {verse?.reference}
              </h2>
              <p className="text-sm text-muted-foreground font-caption">
                {verse?.version} • Added {verse?.dateAdded}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => console.log('Practice verse:', verse)}
              iconName="Brain"
              iconPosition="left"
            >
              Practice
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border overflow-x-auto">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab?.id
                  ? 'border-b-2 border-primary text-primary' :'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab(tab?.id)}
            >
              {tab?.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Success/Error Messages */}
          {(phaseError || successMessage) && (
            <div className={`mb-4 p-3 rounded-lg ${successMessage ? 'bg-success/10 border border-success/30' : 'bg-destructive/10 border border-destructive/30'}`}>
              <p className={`text-sm ${successMessage ? 'text-success' : 'text-destructive'}`}>
                {successMessage || phaseError}
              </p>
            </div>
          )}

          {/* DETAILS TAB */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Verse Text */}
              <div>
                <h3 className="text-base font-semibold text-foreground mb-3">Scripture Text</h3>
                <div className="p-4 bg-muted/50 rounded-lg border border-border">
                  <p className="text-foreground leading-relaxed text-sm">
                    "{verse?.text}"
                  </p>
                  <p className="text-muted-foreground mt-2 text-xs font-medium">
                    — {verse?.reference} ({verse?.version})
                  </p>
                </div>
              </div>

              {/* Status and Progress */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <h4 className="text-sm font-medium text-foreground mb-2">Memorization Status</h4>
                  <div className="flex items-center space-x-2">
                    <Icon name={memStatus?.icon} size={16} className={memStatus?.color} />
                    <span className={`text-sm font-medium ${memStatus?.color}`}>{memStatus?.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {verse?.currentPhase === 0 ? 'Not yet in active memorization' : `Phase ${verse?.currentPhase} of 7`}
                  </p>
                </div>

                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <h4 className="text-sm font-medium text-foreground mb-2">Progress</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Overall Completion</span>
                      <span className="text-sm font-semibold text-foreground">{progressPercentage}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progressPercentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {totalProgressReviews} of 70 successful reviews
                    </p>
                  </div>
                </div>
              </div>

              {/* Key Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/30 rounded-lg p-3 border border-border text-center">
                  <div className="text-lg font-bold text-foreground">{verse?.reviewCount || 0}</div>
                  <div className="text-xs text-muted-foreground">Total Reviews</div>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 border border-border text-center">
                  <div className="text-lg font-bold text-foreground">
                    {verse?.nextReviewDate ? new Date(verse?.nextReviewDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">Next Review</div>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 border border-border text-center">
                  <div className={`text-lg font-bold ${getLastReviewedColor(verse?.lastReviewedAt)}`}>
                    {getLastReviewedLabel(verse?.lastReviewedAt)}
                  </div>
                  <div className="text-xs text-muted-foreground">Last Reviewed</div>
                </div>
              </div>

              {/* Tags */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Tags</h4>
                <Input
                  type="text"
                  placeholder="Enter tags separated by commas"
                  value={tags}
                  onChange={(e) => setTags(e?.target?.value)}
                  description="Use tags to categorize and organize your verses"
                />
              </div>
            </div>
          )}

          {/* SPACED REPETITION TAB */}
          {activeTab === 'spaced-repetition' && (
            <div className="space-y-6">
              <h3 className="text-base font-semibold text-foreground">Phase-Based Memorization</h3>

              <div className="bg-muted/30 rounded-lg p-4 border border-border">
                <h4 className="text-sm font-medium text-foreground mb-3">Current Status</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Phase:</span>
                    <span className="ml-2 font-medium text-foreground">
                      {currentPhase === 0 ? 'Future Verse' : `Phase ${currentPhase}`}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <span className={`ml-2 font-medium ${memStatus?.color}`}>{memStatus?.label}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Reviews:</span>
                    <span className="ml-2 font-medium text-foreground">{reviewCount}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Next Review:</span>
                    <span className="ml-2 font-medium text-foreground">
                      {currentPhase === 0 ? 'Not Scheduled' : verse?.nextReviewDate ? new Date(verse?.nextReviewDate)?.toLocaleDateString() : 'Not Set'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Memorization Phase
                </label>
                <select
                  value={currentPhase}
                  onChange={(e) => handlePhaseChange(Number(e?.target?.value))}
                  disabled={isUpdatingPhase}
                  className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                >
                  <option value={0}>Future Verse (Not Active)</option>
                  <option value={1}>Phase 1 - New Learning</option>
                  <option value={2}>Phase 2 - Initial Practice</option>
                  <option value={3}>Phase 3 - Building Memory</option>
                  <option value={4}>Phase 4 - Strengthening</option>
                  <option value={5}>Phase 5 - Consolidation</option>
                  <option value={6}>Phase 6 - Long-term</option>
                  <option value={7}>Phase 7 - Mastered</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Number of Reviews
                </label>
                <input
                  type="number"
                  min="0"
                  value={reviewCount}
                  onChange={(e) => handleReviewCountChange(Number(e?.target?.value))}
                  disabled={isUpdatingPhase}
                  className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                />
              </div>

              <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
                <h4 className="text-sm font-medium text-foreground mb-2">Phase System Guide</h4>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p><strong className="text-foreground">Future (Phase 0):</strong> Verses not yet in active memorization</p>
                  <p><strong className="text-foreground">Phase 1-2:</strong> Initial learning and practice (30 + 10 reviews)</p>
                  <p><strong className="text-foreground">Phase 3-5:</strong> Memory building and strengthening</p>
                  <p><strong className="text-foreground">Phase 6-7:</strong> Long-term retention and mastery</p>
                </div>
              </div>

              {currentPhase === 0 && (
                <button
                  onClick={() => handlePhaseChange(1)}
                  disabled={isUpdatingPhase}
                  className="w-full bg-success text-success-foreground py-2 px-4 rounded-md hover:bg-success/90 disabled:opacity-50 text-sm font-medium"
                >
                  {isUpdatingPhase ? 'Starting...' : 'Start Memorization'}
                </button>
              )}

              {currentPhase > 0 && (
                <button
                  onClick={() => handlePhaseChange(0)}
                  disabled={isUpdatingPhase}
                  className="w-full bg-muted text-muted-foreground py-2 px-4 rounded-md hover:bg-muted/80 disabled:opacity-50 text-sm font-medium"
                >
                  {isUpdatingPhase ? 'Moving...' : 'Move to Future'}
                </button>
              )}
            </div>
          )}

          {/* NOTES TAB */}
          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-foreground">Personal Notes & Meditation</h3>
                {/* Public / Private toggle */}
                <button
                  type="button"
                  onClick={() => setNotesIsPrivate(prev => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    notesIsPrivate
                      ? 'bg-muted border-border text-muted-foreground hover:bg-muted/80'
                      : 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20'
                  }`}
                  title={notesIsPrivate ? 'Visible only to you — click to make public' : 'Visible to community — click to make private'}
                >
                  <Icon name={notesIsPrivate ? 'Lock' : 'Globe'} size={12} />
                  {notesIsPrivate ? 'Private' : 'Public'}
                </button>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e?.target?.value)}
                placeholder="Write your personal reflections, insights, and meditation notes here..."
                className="w-full h-64 p-4 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {notesIsPrivate
                    ? 'This meditation is private — only you can see it.' :'This meditation is public — it will appear in community reflections.'}
                </p>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSaveNotes}
                  disabled={notesSaving}
                >
                  {notesSaving ? 'Saving…' : 'Save Notes'}
                </Button>
              </div>
              {notesSaveMessage && (
                <p className={`text-xs ${notesSaveMessage?.includes('Failed') ? 'text-destructive' : 'text-success'}`}>
                  {notesSaveMessage}
                </p>
              )}
            </div>
          )}

          {/* LINKED VERSES TAB */}
          {activeTab === 'links' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-foreground">Linked Verses</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowAddLink(!showAddLink); setAddLinkError(''); }}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Link
                </Button>
              </div>

              {/* Add Link Form */}
              {showAddLink && (
                <div className="bg-muted/30 rounded-lg p-4 border border-border space-y-3">
                  <h4 className="text-sm font-medium text-foreground">Link a Verse</h4>

                  {/* Search by reference */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Search any Bible verse (e.g. "John 3:16", "faith", "Romans 8")</label>
                    <input
                      type="text"
                      value={linkSearchQuery}
                      onChange={(e) => handleLinkSearch(e?.target?.value)}
                      placeholder="Type a reference or keyword..."
                      className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>

                  {/* Search results */}
                  {linkSearchLoading && (
                    <p className="text-xs text-muted-foreground">Searching...</p>
                  )}
                  {linkSearchResults?.length > 0 && (
                    <div className="border border-border rounded-md overflow-hidden max-h-48 overflow-y-auto">
                      {linkSearchResults?.map(result => (
                        <button
                          key={result?.id}
                          onClick={() => { setSelectedLinkVerse(result); setLinkSearchQuery(result?.reference); setLinkSearchResults([]); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 border-b border-border last:border-b-0 transition-colors ${
                            selectedLinkVerse?.id === result?.id ? 'bg-primary/10 text-primary' : 'text-foreground'
                          }`}
                        >
                          <span className="font-medium">{result?.reference}</span>
                          <span className="text-muted-foreground ml-2 text-xs">
                            {result?.text?.slice(0, 60)}{result?.text?.length > 60 ? '…' : ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedLinkVerse && (
                    <div className="bg-primary/10 border border-primary/20 rounded-md p-2 text-xs text-primary">
                      Selected: <strong>{selectedLinkVerse?.reference}</strong>
                    </div>
                  )}

                  {/* Relationship label */}
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Relationship</label>
                    <select
                      value={linkRelationship}
                      onChange={(e) => setLinkRelationship(e?.target?.value)}
                      className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option>Related Theme</option>
                      <option>Cross Reference</option>
                      <option>Parallel Passage</option>
                      <option>Fulfillment</option>
                      <option>Contrast</option>
                      <option>Related</option>
                    </select>
                  </div>

                  {addLinkError && (
                    <p className="text-xs text-destructive">{addLinkError}</p>
                  )}

                  <div className="flex gap-2">
                    <Button variant="default" size="sm" onClick={handleAddLink}>
                      Add Link
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setShowAddLink(false); setAddLinkError(''); setSelectedLinkVerse(null); setLinkSearchQuery(''); setLinkSearchResults([]); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Linked verses list */}
              {linkedVersesLoading ? (
                <div className="text-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : linkedVerses?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Icon name="Link" size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No linked verses yet.</p>
                  <p className="text-xs mt-1">Use "Add Link" to connect related verses from anywhere in the Bible.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {linkedVerses?.map((lv) => (
                    <div key={lv?.id} className="p-4 bg-muted/30 rounded-lg border border-border">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-foreground text-sm">{lv?.reference}</h4>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-md">{lv?.relationship}</span>
                          <button
                            onClick={() => handleRemoveLink(lv?.id)}
                            className="text-muted-foreground hover:text-destructive transition-colors"
                            title="Remove link"
                          >
                            <Icon name="X" size={14} />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {lv?.text?.slice(0, 150)}{lv?.text?.length > 150 ? '…' : ''}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* HISTORY TAB */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-foreground">Practice History</h3>
                <span className="text-xs text-muted-foreground">{reviewHistory?.length} sessions</span>
              </div>

              {historyLoading ? (
                <div className="text-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : reviewHistory?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Icon name="Clock" size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No review history yet.</p>
                  <p className="text-xs mt-1">History is recorded when you review this verse in Daily Review.</p>
                </div>
              ) : (
                <>
                  {/* Summary stats */}
                  <div className="grid grid-cols-3 gap-3 mb-2">
                    <div className="bg-success/10 border border-success/20 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold text-success">
                        {reviewHistory?.filter(h => h?.response === 'good')?.length}
                      </div>
                      <div className="text-xs text-muted-foreground">Good</div>
                    </div>
                    <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold text-warning">
                        {reviewHistory?.filter(h => h?.response === 'hard')?.length}
                      </div>
                      <div className="text-xs text-muted-foreground">Hard</div>
                    </div>
                    <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold text-destructive">
                        {reviewHistory?.filter(h => h?.response === 'missed')?.length}
                      </div>
                      <div className="text-xs text-muted-foreground">Missed</div>
                    </div>
                  </div>

                  {/* History list */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {reviewHistory?.map((session, index) => (
                      <div key={session?.id || index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border">
                        <div className="flex items-center space-x-3">
                          <Icon name="Calendar" size={14} className="text-muted-foreground flex-shrink-0" />
                          <div>
                            <span className="text-sm text-foreground font-medium">{formatHistoryDate(session?.reviewed_at)}</span>
                            <span className="text-xs text-muted-foreground ml-2">{formatHistoryTime(session?.reviewed_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${getResponseBadge(session?.response)}`}>
                            {session?.response}
                          </span>
                          {session?.phase_changed && (
                            <span className="text-xs text-primary font-medium">
                              P{session?.phase_before}→P{session?.phase_after}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">
                            Ph.{session?.phase_at_review}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          {/* Delete button at bottom left */}
          {onDelete && (
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(true)}
              iconName="Trash2"
              iconPosition="left"
              className="text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              Delete
            </Button>
          )}
          <div className="flex items-center space-x-3 ml-auto">
            <Button
              variant="outline"
              onClick={() => { setIsEditing(false); onClose(); }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              disabled={isUpdatingPhase}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 bg-destructive/10 rounded-lg">
                  <Icon name="AlertTriangle" size={20} className="text-destructive" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Delete Verse</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-sm text-foreground mb-6">
                Are you sure you want to delete "{verse?.reference}" from your library? All progress data and notes will be permanently removed.
              </p>
              <div className="flex items-center justify-end space-x-3">
                <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                <Button
                  variant="default"
                  onClick={handleDelete}
                  className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                >
                  Delete Verse
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerseDetailModal;